import { RangeSlider, Slider } from "@blueprintjs/core"
import CustomDialog from "components/Atoms/CustomDialog"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import CustomTimePicker from "components/Atoms/CustomTimePicker"
import StaticSelector from "components/Atoms/StaticSelector"
import ButtonMutation from "components/Molecules/ButtonMutation"
import { DAYS } from "constants/calendar"
import dayjs from "dayjs"
import customFormat from "dayjs/plugin/customParseFormat"
import { ADD_UPDATE_DC_STOCK_TAKE } from "graphql/distribution-center/distribution-center.mutations.gql"
import React, { useEffect } from "react"
import { useImmer } from "use-immer"
import { ApolloErrorToast, SuccessToast } from "utils/AppToaster"
import useDCStore from "./dc.store"
dayjs.extend(customFormat)

const CATEGORIES = [
  {
    id: 1,
    name: "Sellable item",
    codename: "SLI",
  },
  {
    id: 2,
    name: "SKU",
    codename: "SKU",
  },
]

const FREQUENCIES = [
  {
    id: 1,
    name: "Daily",
    startDay: 0,
    endDay: 0,
  },
  {
    id: 2,
    name: "Weekly",
    startDay: 1,
    endDay: 7,
  },
  {
    id: 3,
    name: "1st fortnight",
    startDay: 1,
    endDay: 15,
  },
  {
    id: 4,
    name: "2nd fortnight",
    startDay: 16,
    endDay: 31,
  },
  {
    id: 5,
    name: "Monthly",
    startDay: 1,
    endDay: 31,
  },
]

type AddStockTakeDialogProps = {
  isOpen: boolean
  onClose?: () => void
  item?: any
  refetch: () => void
  [key: string]: any
}

function getFrequency(startDay?: number, endDay?: number) {
  if (startDay === 0 && endDay === 0) {
    return FREQUENCIES.find(({ id }) => id === 1)
  } else if (startDay >= 1 && startDay <= 7 && endDay === 0) {
    return FREQUENCIES.find(({ id }) => id === 2)
  } else if (startDay >= 1 && endDay <= 15) {
    return FREQUENCIES.find(({ id }) => id === 3)
  } else if (startDay >= 16 && endDay <= 30) {
    return FREQUENCIES.find(({ id }) => id === 4)
  } else {
    return FREQUENCIES.find(({ id }) => id === 5)
  }
}

const INITIAL_STATE = {
  id: null,
  itemCategory: null,
  startDay: 1,
  startTime: new Date(),
  endDay: 0,
  endTime: new Date(),
  frequency: null,
}
const AddStockTakeDialog = ({
  isOpen,
  onClose,
  item,
  refetch,
}: AddStockTakeDialogProps) => {
  const dcId = useDCStore(store => store.state.basicDetails.id)

  const [state, setMutableState] = useImmer(INITIAL_STATE)

  useEffect(() => {
    if (item) {
      setMutableState(draft => {
        draft.id = item.id
        draft.itemCategory = CATEGORIES.find(
          c => c.codename === item.itemCategory
        )
        draft.startDay = item.startDay
        draft.frequency = getFrequency(item.startDay, item.endDay)
        draft.endDay = item?.endDay
        draft.startTime = dayjs(item.startTime, "HH:mm").toDate()
        draft.endTime = dayjs(item.endTime, "HH:mm").toDate()
      })
    }
  }, [setMutableState, item])

  function handleChange(path: string, value: any) {
    setMutableState(draft => {
      if (path === "frequency") {
        if (value.id === 1) {
          draft.startDay = 0
          draft.endDay = 0
        } else if (value.id === 2) {
          draft.startDay =
            draft.startDay && draft.startDay <= 7 ? draft.startDay : 1
          draft.endDay = 0
        } else if (value.id === 3) {
          draft.startDay =
            draft.startDay <= draft.endDay && draft.endDay <= 15
              ? draft.startDay
              : 1
          draft.endDay =
            draft.endDay > draft.startDay && draft.endDay <= 15
              ? draft.endDay
              : 15
        } else if (value.id === 4) {
          draft.startDay =
            draft.startDay <= draft.endDay && draft.startDay >= 16
              ? draft.startDay
              : 16
          draft.endDay = draft.endDay > draft.startDay ? draft.endDay : 31
        } else {
          draft.startDay = draft.startDay || 1
          draft.endDay = draft.endDay > draft.startDay ? draft.endDay : 31
        }
      }
      draft[path] = value
    })
  }

  return (
    <CustomDialog
      grassUi
      className="top-[20%]"
      title="Add Category"
      isOpen={isOpen}
      footerRightButton={
        <ButtonMutation
          icon="confirm"
          text="Add"
          mutation={ADD_UPDATE_DC_STOCK_TAKE}
          variables={{
            id: state.id,
            distributionCentreId: dcId,
            itemCategory: state.itemCategory?.codename,
            startDay: state.startDay,
            endDay: state.endDay,
            startTime: dayjs(state.startTime).format("HH:mm"),
            endTime: dayjs(state.endTime).format("HH:mm"),
          }}
          onError={err => {
            ApolloErrorToast(err)
          }}
          onCompleted={() => {
            SuccessToast(`Storage take category added successfully`)
            setMutableState(INITIAL_STATE)
            refetch?.()
            onClose()
          }}
        />
      }
      width="50%"
      onClose={onClose}
    >
      <div className="grid grid-cols-2 gap-4">
        <StaticSelector
          label="Category name"
          path="itemCategory"
          items={CATEGORIES}
          value={state?.itemCategory}
          onChange={handleChange}
        />
        <StaticSelector
          label="Frequency"
          path="frequency"
          items={FREQUENCIES}
          value={state?.frequency}
          onChange={handleChange}
        />
        {state?.frequency?.id && (
          <>
            {state.frequency?.id !== 1 && (
              <CustomFormGroup className="col-span-2 mb-0" label="Date Range">
                <div className="px-3">
                  {state.frequency?.id === 2 && (
                    <Slider
                      labelRenderer={value => DAYS[value]?.short || value}
                      showTrackFill={false}
                      value={state.startDay}
                      onChange={value => {
                        setMutableState(prev => {
                          prev.startDay = value
                          prev.endDay = 0
                        })
                      }}
                      min={1}
                      max={7}
                      stepSize={1}
                    />
                  )}
                  {[3, 4, 5].includes(state.frequency?.id) && (
                    <RangeSlider
                      value={[state.startDay, state.endDay]}
                      onChange={range => {
                        setMutableState(prev => {
                          prev.startDay = range[0]
                          prev.endDay = range[1]
                        })
                      }}
                      min={state.frequency?.id === 4 ? 16 : 1}
                      max={state.frequency?.id === 3 ? 15 : 31}
                      stepSize={1}
                    />
                  )}
                </div>
              </CustomFormGroup>
            )}
            <CustomFormGroup label="Start time">
              <div className="flex items-center">
                <p className="mb-0 mr-2 text-sm">
                  {state.frequency?.id === 2
                    ? `${DAYS[state.startDay]?.short} @`
                    : `${dayjs()
                        // This is done because july has 31 days
                        .set("month", 7)
                        .set("date", state.startDay)
                        .format("Do")} day of the
                  month @`}
                </p>
                <CustomTimePicker
                  key={item}
                  path="startTime"
                  value={state?.startTime}
                  onChange={handleChange}
                />
              </div>
            </CustomFormGroup>
            <CustomFormGroup label="End time">
              <div className="flex items-center">
                <p className="mb-0 mr-2 text-sm">
                  {state.frequency?.id === 2
                    ? `${DAYS[state.startDay]?.short} @`
                    : `${dayjs()
                        .set("month", 7)
                        .set("date", state.endDay)
                        .format("Do")} day of the
                  month @`}
                </p>
                <CustomTimePicker
                  path="endTime"
                  value={state?.endTime}
                  onChange={handleChange}
                />
              </div>
            </CustomFormGroup>
          </>
        )}
      </div>
    </CustomDialog>
  )
}

export default AddStockTakeDialog
